"use strict";
/**
 * Constantes de regra de negócio.
 *
 * Ficam num pacote compartilhado para que API e app apliquem exatamente os
 * mesmos limites — o app antecipa o erro na UI, a API é quem realmente decide.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PAGINATION = exports.SECURITY = exports.FINANCE = exports.INVITE = exports.GAME_LIMITS = exports.GROUP_LIMITS = exports.TEAM_PRESETS = exports.TEAM_BALANCE = exports.RATING_ALGORITHM = exports.SKILL_LABELS_PT = exports.SKILL = void 0;
exports.skillLabel = skillLabel;
// ---------------------------------------------------------------------------
// Habilidade do jogador
// ---------------------------------------------------------------------------
exports.SKILL = {
    MIN: 1.0,
    MAX: 5.0,
    /** Nota atribuída a quem entra no grupo sem avaliação prévia. */
    DEFAULT: 3.0,
    /** Granularidade aceita nas avaliações (meia estrela). */
    STEP: 0.5,
    /** Casas decimais mantidas na nota consolidada. */
    PRECISION: 2,
};
exports.SKILL_LABELS_PT = [
    { max: 1.4, label: 'Iniciante' },
    { max: 2.4, label: 'Básico' },
    { max: 3.4, label: 'Intermediário' },
    { max: 4.4, label: 'Avançado' },
    { max: 5.0, label: 'Excelente' },
];
function skillLabel(value) {
    return exports.SKILL_LABELS_PT.find((entry) => value <= entry.max)?.label ?? 'Intermediário';
}
// ---------------------------------------------------------------------------
// Algoritmo de notas (média ponderada com amortecimento)
// ---------------------------------------------------------------------------
exports.RATING_ALGORITHM = {
    /**
     * Peso do histórico consolidado, expresso em "avaliações equivalentes".
     * Quanto maior, mais lenta é a variação da nota — evita que uma avaliação
     * isolada mova o jogador de patamar.
     */
    PRIOR_WEIGHT: 12,
    /** Variação máxima de nota permitida por partida. */
    MAX_CHANGE_PER_GAME: 0.25,
    /** Mínimo de avaliações numa partida para que ela afete a nota. */
    MIN_RATINGS_TO_APPLY: 3,
    /**
     * Mínimo de avaliações antes de exibir a contagem ao avaliado.
     * Abaixo disso o avaliado poderia deduzir quem avaliou.
     */
    MIN_RATINGS_TO_REVEAL_COUNT: 5,
    /**
     * Meia-vida em dias do peso de uma avaliação: avaliações recentes pesam mais
     * que antigas ao recalcular a nota a partir do histórico.
     */
    RECENCY_HALF_LIFE_DAYS: 90,
    /** Descarta a maior e a menor nota quando há avaliações suficientes. */
    TRIM_OUTLIERS_MIN_SAMPLE: 6,
};
// ---------------------------------------------------------------------------
// Formação de times
// ---------------------------------------------------------------------------
exports.TEAM_BALANCE = {
    /** Peso do equilíbrio de força na função de custo. */
    WEIGHT_SKILL: 1.0,
    /** Peso da distribuição de goleiros — prioridade máxima. */
    WEIGHT_GOALKEEPER: 100.0,
    /** Peso da diferença de tamanho entre os times. */
    WEIGHT_SIZE: 50.0,
    /** Peso de evitar concentrar os jogadores mais fortes num único time. */
    WEIGHT_TOP_HEAVY: 0.6,
    /**
     * Peso de espalhar os setores (defesa/meio/ataque) entre os times.
     *
     * Fica abaixo do goleiro e do tamanho de propósito: um time só de zagueiros
     * joga mal, mas ainda joga — já um time sem goleiro trava a pelada. Também
     * fica abaixo da força, porque na pelada de fim de semana quase todo mundo
     * se vira em qualquer posição; o setor é um refinamento, não uma regra dura.
     */
    WEIGHT_SECTOR: 3.0,
    /** Peso de evitar repetir exatamente a mesma escalação da última partida. */
    WEIGHT_REPETITION: 0.4,
    /** Iterações de melhoria local por reinício. */
    MAX_ITERATIONS: 2000,
    /** Reinícios aleatórios — o melhor resultado vence. */
    RESTARTS: 12,
    /** Diferença de média considerada "equilibrado o suficiente" para parar cedo. */
    ACCEPTABLE_SKILL_SPREAD: 0.05,
    /** Quantas partidas anteriores entram no cálculo de repetição. */
    REPETITION_LOOKBACK_GAMES: 3,
};
exports.TEAM_PRESETS = [
    { name: 'Time A', color: '#12B76A' },
    { name: 'Time B', color: '#2E90FA' },
    { name: 'Time C', color: '#F79009' },
    { name: 'Time D', color: '#F04438' },
    { name: 'Time E', color: '#9E77ED' },
    { name: 'Time F', color: '#EAAA08' },
];
// ---------------------------------------------------------------------------
// Limites de grupo e partida
// ---------------------------------------------------------------------------
exports.GROUP_LIMITS = {
    NAME_MIN: 3,
    NAME_MAX: 60,
    DESCRIPTION_MAX: 500,
    RULES_MAX: 4000,
    MIN_PLAYERS: 4,
    MAX_PLAYERS: 60,
    MIN_PLAYERS_PER_TEAM: 2,
    MAX_PLAYERS_PER_TEAM: 15,
    MAX_GROUPS_PER_USER: 20,
    MAX_ADMINS: 10,
    /** Mensalidade máxima aceita — barreira contra erro de digitação. */
    MAX_MONTHLY_FEE_CENTS: 100_000_00,
};
exports.GAME_LIMITS = {
    MAX_GUESTS_PER_GAME: 20,
    MAX_GUESTS_PER_MEMBER: 3,
    NOTES_MAX: 500,
    /** Janela padrão (horas) para avaliar após o fim da partida. */
    DEFAULT_RATING_WINDOW_HOURS: 48,
    /** Prazo padrão (minutos) para o primeiro da fila aceitar a vaga. */
    DEFAULT_WAITLIST_RESPONSE_MINUTES: 60,
    /** Quantas horas antes a lista de presença abre. */
    DEFAULT_ATTENDANCE_OPENS_HOURS_BEFORE: 120,
    /** Quantas horas antes a lista fecha. */
    DEFAULT_ATTENDANCE_CLOSES_HOURS_BEFORE: 2,
    /** Antecedência da criação automática da partida recorrente. */
    DEFAULT_AUTO_CREATE_DAYS_AHEAD: 7,
    MAX_SCORE: 99,
};
// ---------------------------------------------------------------------------
// Convites
// ---------------------------------------------------------------------------
exports.INVITE = {
    /** Bytes de entropia do token — 16 bytes ≈ 128 bits. */
    TOKEN_BYTES: 16,
    CODE_LENGTH: 22,
    DEFAULT_TTL_DAYS: 7,
    MAX_TTL_DAYS: 90,
};
// ---------------------------------------------------------------------------
// Financeiro
// ---------------------------------------------------------------------------
exports.FINANCE = {
    CURRENCY: 'BRL',
    LOCALE: 'pt-BR',
    MIN_DUE_DAY: 1,
    MAX_DUE_DAY: 28,
    /** Dias antes do vencimento em que o lembrete é disparado. */
    FEE_REMINDER_DAYS_BEFORE: 3,
    /** Dias de tolerância após o vencimento antes de marcar como atrasado. */
    FEE_GRACE_DAYS: 0,
    /**
     * Prazo de quem recebe a mensalidade depois do dia de vencimento (grupo
     * criado no meio do mês, admin que gerou atrasado). Sem isso a cobrança
     * nasceria vencida e viraria "em atraso" na manhã seguinte.
     */
    LATE_FEE_DUE_DAYS: 5,
    MAX_EXPENSE_CENTS: 1_000_000_00,
    EXPENSE_DESCRIPTION_MAX: 200,
    RECEIPT_MAX_SIZE_BYTES: 5 * 1024 * 1024,
};
// ---------------------------------------------------------------------------
// Segurança
// ---------------------------------------------------------------------------
exports.SECURITY = {
    PASSWORD_MIN_LENGTH: 8,
    PASSWORD_MAX_LENGTH: 128,
    /** Parâmetros do Argon2id — ~64 MiB e 3 iterações. */
    ARGON2: {
        memoryCost: 65536,
        timeCost: 3,
        parallelism: 4,
    },
    MAX_SESSIONS_PER_USER: 10,
    EMAIL_VERIFICATION_TTL_HOURS: 24,
    PASSWORD_RESET_TTL_MINUTES: 30,
    /** Tamanho máximo aceito em uploads de imagem. */
    MAX_UPLOAD_BYTES: 5 * 1024 * 1024,
    ALLOWED_IMAGE_MIME: ['image/jpeg', 'image/png', 'image/webp', 'image/heic'],
};
// ---------------------------------------------------------------------------
// Paginação
// ---------------------------------------------------------------------------
exports.PAGINATION = {
    DEFAULT_LIMIT: 20,
    MAX_LIMIT: 100,
};
//# sourceMappingURL=domain-rules.js.map